import type NoEndRecurrence from 'owa-service/lib/contract/NoEndRecurrence';
import type NumberedRecurrence from 'owa-service/lib/contract/NumberedRecurrence';
import type RecurrencePatternBaseType from 'owa-service/lib/contract/RecurrencePatternBaseType';
import type RecurrenceRangeBaseType from 'owa-service/lib/contract/RecurrenceRangeBaseType';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import type RegeneratingPatternBaseType from 'owa-service/lib/contract/RegeneratingPatternBaseType';
import type RelativeMonthlyRecurrence from 'owa-service/lib/contract/RelativeMonthlyRecurrence';
import type RelativeYearlyRecurrence from 'owa-service/lib/contract/RelativeYearlyRecurrence';
import type WeeklyRecurrence from 'owa-service/lib/contract/WeeklyRecurrence';
import type AbsoluteMonthlyRecurrence from 'owa-service/lib/contract/AbsoluteMonthlyRecurrence';
import type AbsoluteYearlyRecurrence from 'owa-service/lib/contract/AbsoluteYearlyRecurrence';
import type EndDateRecurrence from 'owa-service/lib/contract/EndDateRecurrence';
import type * as Schema from 'owa-graph-schema';
import type DailyRecurrence from 'owa-service/lib/contract/DailyRecurrence';

// TODO VSO 117161: Pull recurrence types out of recurrence-utils for reuse in resolvers
// reuse types from shared package here

/**
 * We need to handle this manually because
 * RecurrencePatternBaseType is an empty object that any number of classes may
 * subclass, but Schema.RecurrencePatternType is a union of possible object types.
 * They can't be manually mapped between.
 *
 * We also have to add explicit __typename fields, since the client has to be able to
 * distinguish the value into one of the union members.
 */
function recurrencePattern(
    recurrencePattern: RecurrencePatternBaseType
): Schema.RecurrencePatternType {
    switch (recurrencePattern.__type) {
        case 'AbsoluteYearlyRecurrence:#Exchange': {
            return {
                __typename: 'AbsoluteYearlyRecurrence',
                ...(recurrencePattern as AbsoluteYearlyRecurrence),
            };
        }
        case 'AbsoluteMonthlyRecurrence:#Exchange': {
            return {
                __typename: 'AbsoluteMonthlyRecurrence',
                ...(recurrencePattern as AbsoluteMonthlyRecurrence),
            };
        }
        case 'WeeklyRecurrence:#Exchange': {
            return {
                __typename: 'WeeklyRecurrence',
                ...(recurrencePattern as WeeklyRecurrence),
            };
        }
        case 'DailyRecurrence:#Exchange': {
            return {
                __typename: 'DailyRecurrence',
                ...(recurrencePattern as DailyRecurrence),
            };
        }
        case 'RegeneratingPatternBaseType:#Exchange': {
            return {
                __typename: 'RegeneratingPatternBaseType',
                ...(recurrencePattern as RegeneratingPatternBaseType),
            };
        }
        case 'RelativeYearlyRecurrence:#Exchange': {
            return {
                __typename: 'RelativeYearlyRecurrence',
                ...(recurrencePattern as RelativeYearlyRecurrence),
            };
        }
        case 'RelativeMonthlyRecurrence:#Exchange': {
            return {
                __typename: 'RelativeMonthlyRecurrence',
                ...(recurrencePattern as RelativeMonthlyRecurrence),
            };
        }
    }
    throw new Error(`unhandled recurrence pattern ${recurrencePattern.__type}`);
}

/**
 * Maps between known implementers of the OWS base class RecurrenceRangeBaseType and
 * the union of supported subtypes from the SChema
 *
 * See the docstring on recurrencePattern() for details
 */
function recurrenceRange(recurrenceRange: RecurrenceRangeBaseType): Schema.RecurrenceRangeType {
    switch (recurrenceRange.__type) {
        case 'EndDateRecurrence:#Exchange': {
            return { __typename: 'EndDateRecurrence', ...(recurrenceRange as EndDateRecurrence) };
        }
        case 'NoEndRecurrence:#Exchange': {
            return { __typename: 'NoEndRecurrence', ...(recurrenceRange as NoEndRecurrence) };
        }
        case 'NumberedRecurrence:#Exchange': {
            return { __typename: 'NumberedRecurrence', ...(recurrenceRange as NumberedRecurrence) };
        }
    }
    throw new Error(`unhandled recurrence range ${recurrenceRange.__type}`);
}

export function convertRecurrenceTypeToGql(recurrenceType: RecurrenceType): Schema.RecurrenceType {
    return {
        __typename: 'RecurrenceType',
        RecurrencePattern: recurrencePattern(recurrenceType.RecurrencePattern),
        RecurrenceRange: recurrenceRange(recurrenceType.RecurrenceRange),
    };
}
